/**
 * Shared vocabulary for the Approvals view — status maps, filter options,
 * date helpers and the small presentational bits that both the table and the
 * card grid render, so the two views can never drift apart on what a status
 * looks like or what a row is allowed to do.
 */
import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { formatDistanceToNowStrict } from "date-fns";

// "All" placed first (not appended) below so it stays the visible default
// option for every dropdown filter.
export const VIDEO_SOURCE_OPTIONS = ["ALL", "ORIGINAL", "LOCAL", "EDITED"];
export const PUSH_STATUS_OPTIONS = ["ALL", "PENDING", "QUEUED", "PUBLISHED", "FAILED"];
export const FORCE_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "FORCE", label: "Force" },
  { value: "NORMAL", label: "Normal" },
];
export const SORT_OPTIONS = [
  { value: "NEWEST", label: "Newest first" },
  { value: "OLDEST", label: "Oldest first" },
  { value: "TITLE", label: "Title A–Z" },
];

export const APPROVAL_STATUS = {
  PENDING_APPROVAL: { label: "Awaiting Approval", color: "#8b5cf6", bg: "#f5f3ff" },
  APPROVED: { label: "Approved", color: "#2563eb", bg: "#eff6ff" },
  REJECTED: { label: "Rejected", color: "#dc2626", bg: "#fef2f2" },
};

// Deliberately just reports HOW it was sent (force vs. queue), not what
// happens to it afterward inside Admin Portal — CreatorVault doesn't poll
// Admin Portal for downstream lifecycle (e.g. whether a queued item later
// actually got popped and published by Admin Portal's own cron); QUEUED/
// PUBLISHED here only ever mean "the push call to Admin Portal itself
// succeeded," as a non-force/force send respectively.
export const PUSH_STATUS = {
  PENDING: { label: "Pending", color: "#64748b", bg: "#f8fafc" },
  QUEUED: { label: "Sent to Queue", color: "#2563eb", bg: "#eff6ff" },
  PUBLISHED: { label: "Sent Forcefully", color: "#16a34a", bg: "#f0fdf4" },
  FAILED: { label: "Failed", color: "#dc2626", bg: "#fef2f2" },
};

// Edited-video render status, EDITED-source rows only — mirrors the language
// used elsewhere in this app (RephrasedContentReview/scheduler render states).
export const RENDER_STATUS = {
  rendering: { label: "Rendering…", color: "#d97706", bg: "#fffbeb" },
  render_complete: { label: "Render Ready", color: "#16a34a", bg: "#f0fdf4" },
  render_failed: { label: "Render Failed", color: "#dc2626", bg: "#fef2f2" },
};

export const VIDEO_SOURCE_META = {
  ORIGINAL: { label: "Original", color: "#0f766e", bg: "#f0fdfa" },
  LOCAL: { label: "Local Upload", color: "#7c3aed", bg: "#f5f3ff" },
  EDITED: { label: "Edited", color: "#c2410c", bg: "#fff7ed" },
};

// Colour/label per history entry status, so a timeline reads at a glance.
// Anything unmapped falls through to a neutral grey — the backend can add
// new step names without this needing to know about them first.
export const LOG_STEP = {
  SUBMITTED: { label: "Submitted", color: "#8b5cf6" },
  APPROVED: { label: "Approved", color: "#2563eb" },
  REJECTED: { label: "Rejected", color: "#dc2626" },
  ASSET_PROMOTED: { label: "Moved to final storage", color: "#0d9488" },
  SENDING: { label: "Sending to Admin Portal", color: "#d97706" },
  QUEUED: { label: "Sent to Queue", color: "#2563eb" },
  PUBLISHED: { label: "Sent Forcefully", color: "#16a34a" },
  FAILED: { label: "Failed", color: "#dc2626" },
  RETRY_QUEUED: { label: "Re-queued for retry", color: "#f59e0b" },
  MEDIA_DELETED: { label: "Files deleted", color: "#64748b" },
};

// A push still awaiting a decision after this long is called out in the UI —
// not an error, just the thing an approver most likely wants to see first.
export const STALE_PENDING_HOURS = 48;

export const parseDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "3 hours ago" — via date-fns so it stays honest about plurals/units. */
export const relativeTime = (iso) => {
  const d = parseDate(iso);
  if (!d) return "—";
  return `${formatDistanceToNowStrict(d)} ago`;
};

export const absoluteTime = (iso) => {
  const d = parseDate(iso);
  return d ? d.toLocaleString() : "Unknown date";
};

export const hoursSince = (iso) => {
  const d = parseDate(iso);
  return d ? (Date.now() - d.getTime()) / 36e5 : 0;
};

/**
 * Everything the row-level UI needs to decide what it may offer, in one place
 * so the table and the cards can't disagree about (say) whether Approve is
 * allowed. The render gate mirrors AdminPortalPushApproveView exactly — keep
 * this in sync with that view if either changes.
 */
export function getRowState(row, isAdmin) {
  const isPending = row.approval_status === "PENDING_APPROVAL";
  const isEdited = row.video_source === "EDITED";
  const renderBlocksApproval = isEdited && row.render_status !== "render_complete";
  const renderFailed = row.render_status === "render_failed";
  const canApprove = Boolean(isAdmin && isPending && !renderBlocksApproval);
  const approveDisabledReason = renderBlocksApproval
    ? renderFailed
      ? "Video render failed — reject and resubmit instead"
      : `Waiting for video render to finish (${RENDER_STATUS[row.render_status]?.label || row.render_status || "starting"})`
    : "Approve — will be sent to Admin Portal shortly";

  return {
    isPending,
    isEdited,
    renderBlocksApproval,
    renderFailed,
    canApprove,
    approveDisabledReason,
    hasFailed: row.status === "FAILED",
    canRetry: Boolean(isAdmin && row.status === "FAILED"),
    canDelete: Boolean(isAdmin && row.approval_status === "REJECTED"),
    // Waiting on a human, and waiting a while — surfaced as an "ageing" cue
    // rather than an alarm, since nothing is actually broken.
    isStale: isPending && hoursSince(row.created_date) > STALE_PENDING_HOURS,
    title: row.video_title || row.video?.title || row.unique_key || row.video?.unique_key || "Untitled",
    // What Admin Portal gets — differs from `title` when it was rephrased.
    sent: {
      title: row.title_sent || "",
      description: row.description_sent || "",
      tags: Array.isArray(row.tags_sent) ? row.tags_sent.join(", ") : row.tags_sent || "",
    },
    key: row.unique_key || row.video?.unique_key || "",
    target: row.admin_portal_feed_title || row.admin_portal_feed_id || "—",
    canPreview: Boolean(row.preview_url || row.thumbnail_url || isEdited),
  };
}

/**
 * Hover content for a row's title: the original, then what was sent to Admin
 * Portal (rephrased/edited in Step 4). Just the original when nothing differs.
 */
export function SentContentTooltip({ s }) {
  const { title, description, tags } = s.sent;
  if (!title && !description && !tags) return s.title;
  const line = (label, value) =>
    value ? (
      <Box mt={0.75}>
        <Typography component="div" sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.3 }}>
          {label}
        </Typography>
        <Typography component="div" sx={{ fontSize: 12.5, whiteSpace: "pre-line", display: "-webkit-box", WebkitLineClamp: 6, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {value}
        </Typography>
      </Box>
    ) : null;
  return (
    <Box sx={{ maxWidth: 360 }}>
      {line("Original title", s.title)}
      {line("Sent title", title)}
      {line("Sent description", description)}
      {line("Sent tags", tags)}
    </Box>
  );
}

/** Status pill with a leading dot — reads at a glance in a dense list. */
export function StatusChip({ map, value, size = "small" }) {
  const s = map[value] || { label: value || "—", color: "#64748b", bg: "#f8fafc" };
  return (
    <Chip
      size={size}
      label={s.label}
      icon={
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: s.color, ml: "8px !important", mr: "-2px !important" }}
        />
      }
      sx={{
        color: s.color,
        bgcolor: s.bg,
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: 0.1,
        height: 22,
        border: `1px solid ${s.color}2e`,
        "& .MuiChip-label": { px: 0.9 },
      }}
    />
  );
}

/**
 * Created date, the way it's actually useful: relative up front (how long has
 * this been sitting there), exact on hover (for the audit trail).
 */
export function TimeAgo({ iso, prefix = "", stale = false, variant = "caption", sx }) {
  if (!iso) {
    return (
      <Typography variant={variant} color="text.disabled" sx={sx}>
        —
      </Typography>
    );
  }
  return (
    <Tooltip title={absoluteTime(iso)} placement="top">
      <Typography
        component="span"
        variant={variant}
        sx={{
          cursor: "help",
          fontVariantNumeric: "tabular-nums",
          color: stale ? "#b45309" : "text.secondary",
          fontWeight: stale ? 600 : 400,
          ...sx,
        }}
      >
        {prefix}
        {relativeTime(iso)}
      </Typography>
    </Tooltip>
  );
}
